import { OwnableTheme } from '@Theme';
import React from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import { Platform } from 'react-native';
import styled, { css } from 'styled-components/native';
import { switchProp } from 'styled-tools';

// TODO - sizes to be implemented when we have specification
export type Size = keyof typeof OwnableTheme.fontSizes;
export type Weight = 'bold' | 'light' | 'regular';

type StyledTextProps = {
  weight?: Weight;
  size?: Size;
  $gutterBottom?: number;
  padding?: number;
  color?: string;
  align?: 'center' | 'left' | 'right';
  ellipsisLines?: number | null;
  underline?: boolean;
  capitalise?: boolean;
};

const StyledText = styled.Text<StyledTextProps>`
  ${({ ellipsisLines }) =>
    ellipsisLines === 1 &&
    Platform.OS === 'web' &&
    css`
      white-space: nowrap;
    `}
  ${switchProp('weight', {
    light: css`
      font-family: ${OwnableTheme.fonts.gilroy.light};
    `,
    regular: css`
      font-family: ${OwnableTheme.fonts.gilroy.regular};
    `,
    bold: css`
      font-family: ${OwnableTheme.fonts.gilroy.bold};
    `,
  })};
  color: ${({ color }) => `${color}`};
  margin-bottom: ${({ $gutterBottom }) => `${$gutterBottom}px`};
  ${({ size }) =>
    size &&
    css`
      font-size: ${OwnableTheme.fontSizes[size]};
    `}
  ${switchProp('align', {
    left: css`
      text-align: left;
    `,
    center: css`
      text-align: center;
    `,
    right: css`
      text-align: right;
    `,
  })}
  ${({ underline }) => underline && { textDecoration: 'underline' }};
  ${({ capitalise }) => capitalise && { textTransform: 'capitalize' }}
`;

type TextProps = {
  weight?: Weight;
  size?: Size;
  gutterBottom?: number;
  padding?: number;
  color?: string;
  align?: 'center' | 'left' | 'right';
  ellipsisLines?: number | null;
  underline?: boolean;
  capitalise?: boolean;
  fontStyleOverride?: StyleProp<TextStyle>;
  children: React.ReactNode; // Required for React 18
};
export const Text: React.FC<TextProps> = ({
  children,
  size,
  weight = 'regular',
  gutterBottom = 0,
  color = OwnableTheme.colors.persianIndigo,
  align = 'left',
  padding = 0,
  ellipsisLines = null,
  underline = false,
  capitalise = false,
  fontStyleOverride = {},
}) => (
  <StyledText
    ellipsizeMode='tail'
    ellipsisLines={ellipsisLines}
    numberOfLines={ellipsisLines ?? undefined}
    $gutterBottom={gutterBottom}
    weight={weight}
    size={size}
    color={color}
    align={align}
    padding={padding}
    underline={underline}
    capitalise={capitalise}
    textBreakStrategy='simple'
    style={fontStyleOverride}
  >
    {children}
  </StyledText>
);
